import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { IconTrash, IconCopy, IconAlertTriangle } from '@tabler/icons-react'
import { startCase } from 'lodash'

import { Toggle } from '@/components/forms/Checkbox'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { getOrganizationOptions, formatTime } from '@/utils/helpers'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { truncatedColumn } from '@/components/helpers/tableColumnHelpers'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import {
  copyScorecard,
  deleteScorecard,
  updateScorecardSettings,
  toggleScorecardActive,
} from '@/reducers/scorecards/scorecards.actions'
import { closeModal } from '@/reducers/ui/ui.redux'

import { formatScorecardType, scorecardHasCriteria } from './helpers/index'

import './scorecards.css'

export const ScorecardsTable = ({ scorecardType, isOmniChannelEnabled = false }) => {
  const dispatch = useDispatch()
  const { data, loading } = useSelector((state) => state.scorecards)
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const { organizations } = useSelector((state) => state)

  const [openNoCriteriaModal, setOpenNoCriteriaModal] = useState(false)
  const [openActivateModal, setOpenActivateModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [targetConfig, setTargetConfig] = useState({})

  const isBaltoAdmin = currentUserOrgId === 1
  const isAutomated = scorecardType === 'automated'

  let url = `scorecards/edit_config`

  if (scorecardType === 'copilot') {
    url = `qa-copilot`
  } else if (scorecardType === 'coaching') {
    url = `/coaching/scorecards`
  }

  const handleCloseActivateModal = () => {
    setTargetConfig({})
    setOpenActivateModal(false)
  }

  const handleCloseNoCriteriaModal = () => {
    setTargetConfig({})
    setOpenNoCriteriaModal(false)
  }

  const handleCloseDeletionModal = () => {
    setTargetConfig({})
    setOpenDeleteModal(false)
  }

  const softDelete = () => {
    dispatch(deleteScorecard(targetConfig.id, targetConfig.organization_id, scorecardType))
    handleCloseDeletionModal()
  }

  const openActiveModal = (scorecardConfig) => {
    const hasCriteria = scorecardHasCriteria(scorecardConfig)
    if (hasCriteria) {
      setTargetConfig(scorecardConfig)
      setOpenActivateModal(true)
    } else {
      setTargetConfig(scorecardConfig)
      setOpenNoCriteriaModal(true)
    }
  }
  const setAgentViewable = (scorecardConfig) => {
    dispatch(
      updateScorecardSettings(
        scorecardConfig,
        { agent_viewable: !scorecardConfig.agent_viewable },
        scorecardType
      )
    )
  }

  const openDeletionModal = (e, row) => {
    setTargetConfig(row)
    setOpenDeleteModal(true)
  }

  const toggleActiveStatus = () => {
    dispatch(toggleScorecardActive(targetConfig, scorecardType))
    handleCloseActivateModal()
  }

  const createScorecardCopy = async (formValues, row) => {
    if (!formValues.organization_id) {
      return
    }

    const scorecardConfig = data.scorecardConfigs.find((scorecard) => scorecard?.sid === row?.sid)
    const existingCopies = data.scorecardConfigs.filter((scorecard) =>
      scorecard.name.includes(`COPY OF ${row.name.value}`)
    )

    dispatch(
      copyScorecard(
        scorecardConfig,
        existingCopies.length,
        scorecardType,
        formValues.organization_id
      )
    )
    dispatch(closeModal())
  }

  const getCriteriaCount = (sections) => {
    if (!Array.isArray(sections)) return 0

    return sections.reduce((total, section) => {
      const measures = section.measures || []
      return (
        total +
        measures.reduce((measureTotal, measure) => {
          const criteria = measure.criteria || []
          return measureTotal + criteria.length
        }, 0)
      )
    }, 0)
  }

  let columns = [
    {
      accessor: 'name',
      label: 'Scorecard Name',
      isSearchable: true,
    },
    ...(isOmniChannelEnabled && scorecardType === 'copilot'
      ? [
          {
            accessor: 'call_type',
            label: 'Channel',
            isSearchable: true,
            format: (value) => startCase(value),
          },
        ]
      : []),
    {
      accessor: 'active',
      label: 'Active',
    },
    {
      accessor: 'agent_viewable',
      label: 'Visible to Agents',
    },
    {
      accessor: 'sections',
      label: 'Criteria Count',
      format: (value) => (
        <div data-testid="criteria-count" className="scorecard-criteria-count">
          {value}
        </div>
      ),
    },
  ]

  if (isAutomated) {
    columns.push({
      accessor: 'config_name',
      label: 'Playbook',
      isSearchable: true,
      format: (playbook) => truncatedColumn(playbook),
    })
  }

  columns = columns.concat([
    { accessor: 'created_by_full_name', label: 'Created By' },
    {
      accessor: 'created_on',
      label: 'Created On',
      format: (value) => formatTime(value),
    },
    {
      accessor: 'last_modified_by',
      label: 'Last Modified By',
    },
    {
      accessor: 'modified_on',
      label: 'Last Modified',
      format: (value) => formatTime(value),
    },
  ])

  const actions = [{ label: 'Delete', fn: openDeletionModal, icon: <IconTrash /> }]

  // Only Balto Admins can clone non-automated scorecards
  // Anyone can clone automated scorecards
  actions.push({
    label: 'Clone Scorecard',
    trigger: (row) => {
      // Only show for non-automated if admin, or for automated if scorecardType === 'automated'
      if (isAutomated) {
        return (
          <ButtonAndFormModal
            popup
            icon={<IconCopy />}
            modalTitle="Clone Scorecard"
            modalId={`scorecards/clone-${row.id}`}
            modalProps={{ size: 'tiny' }}
            modalContent={
              <p>
                Would you like to make a copy of <strong>{row.name.value}</strong>?
              </p>
            }
            buttonProps={{
              basic: true,
              small: true,
              square: true,
              'data-testid': `scorecards/clone-${row.id}-button`,
            }}
            form={
              <div className="flex-align-center flex-end">
                <Button
                  brand
                  data-testid={`scorecards/clone-${row.id}-confirm`}
                  onClick={() => {
                    createScorecardCopy({ organization_id: row.organization_id }, row)
                  }}
                >
                  Yes, make a copy
                </Button>
              </div>
            }
          />
        )
      }
      if (isBaltoAdmin && !isAutomated) {
        return (
          <ButtonAndFormModal
            popup
            icon={<IconCopy />}
            modalTitle="Clone Scorecard"
            modalId={`scorecards/clone-${row.id}`}
            modalProps={{ size: 'tiny' }}
            modalContent={
              <p>
                Clone <strong>{row.name.value}</strong> to the following organization:
              </p>
            }
            buttonProps={{
              basic: true,
              small: true,
              square: true,
              'data-testid': `scorecards/clone-${row.id}-button`,
            }}
            form={
              <AbstractForm
                existingValues={{ organization_id: row.organization_id }}
                schema={[
                  {
                    name: 'organization_id',
                    type: 'select',
                    label: 'Organization',
                    options: getOrganizationOptions(organizations),
                    required: true,
                  },
                ]}
                onSubmit={(formValues) => createScorecardCopy(formValues, row)}
                buttonLabel="Save"
                onClose={() => dispatch(closeModal())}
                isModal
              />
            }
          />
        )
      }
      return null
    },
  })

  const rows = data.scorecardConfigs.map((scorecard) => ({
    ...scorecard,
    sections: getCriteriaCount(scorecard.sections),
    index: scorecard.id,
    active: {
      as: (
        <Toggle
          data-testid="scorecard-active-toggle"
          aria-label={`Active: ${scorecard.name}`}
          checked={scorecard.active}
          onChange={() => openActiveModal(scorecard)}
        />
      ),
      value: scorecard.active,
    },
    agent_viewable: {
      as: (
        <Toggle
          data-testid="scorecard-agent-viewable-toggle"
          aria-label={`Visible to agents: ${scorecard.name}`}
          checked={scorecard.agent_viewable}
          onChange={() => setAgentViewable(scorecard)}
        />
      ),
      value: scorecard.agent_viewable,
    },
    name: {
      as: (
        <div className="editor-page-warning">
          {!scorecard.playbook_up_to_date && (
            <InteractiveTooltip content="This scorecard is out of date with its playbook. You can update it in edit mode.">
              <IconAlertTriangle
                size={16}
                className="status-warning"
                data-testid="playbook-not-up-to-date-warning"
              />
            </InteractiveTooltip>
          )}
          <Link to={`${url}/${scorecard.id}`} className="table-link">
            {scorecard.name}
          </Link>
        </div>
      ),
      value: scorecard.name,
    },
    type: formatScorecardType(scorecard.type),
  }))
  return (
    <div data-testid="scorecards-table">
      <Card className="no-padding">
        <AdvancedTable
          loading={loading.scorecards}
          rows={rows}
          columns={columns}
          actions={actions}
          defaultOrderBy="modified_on"
          defaultOrder="desc"
          pagination
          stickyAction
        />
      </Card>

      {openDeleteModal && (
        <BasicModal
          title={`Delete ${targetConfig.name.value}`}
          onClose={handleCloseDeletionModal}
          show={openDeleteModal}
          size="tiny"
        >
          <div data-testid="confirm-delete-modal">
            {`Are you sure you want to delete "${targetConfig.name.value}"?`}
          </div>
          <ModalActions onClose={handleCloseDeletionModal} onSubmit={softDelete} isDelete />
        </BasicModal>
      )}

      {openActivateModal && (
        <BasicModal
          title={`${targetConfig.active ? 'Deactivate' : 'Activate'} Scorecard Configuration`}
          onClose={handleCloseActivateModal}
          show={openActivateModal}
          size="tiny"
        >
          <div data-testid="confirm-active-modal">
            {`Are you sure you want to ${
              targetConfig.active ? 'deactivate' : 'activate'
            } this scorecard configuration?
              If you do, it will ${targetConfig.active ? 'not' : ''} be used to score calls.`}
          </div>
          <ModalActions
            onClose={handleCloseActivateModal}
            onSubmit={toggleActiveStatus}
            buttonLabel={`${targetConfig.active ? 'Deactivate' : 'Activate'}`}
          />
        </BasicModal>
      )}

      {openNoCriteriaModal && (
        <BasicModal
          title="Configure Scorecard"
          onClose={handleCloseNoCriteriaModal}
          show={openNoCriteriaModal}
          data-testid="no-criteria-modal"
          size="tiny"
        >
          <p>This scorecard cannot become active until one or more measures are in use.</p>

          <p>Please select the criteria you would like Balto to score calls with.</p>
        </BasicModal>
      )}
    </div>
  )
}
